import {useCallback, useContext} from 'react';
import {tokenize, splitHighlight} from '@embertracking/common/data/search/matcher.mjs';

import {
    MEMBER_SECTIONS,
    visibleMemberSections,
    memberSectionPath,
    memberBasePath,
    DEFAULT_MEMBER_SECTION
} from '-/pages/member/sections';
import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {isTrackingEnabled} from '-/data/driverHours';
import {hasFeature} from '-/features';

// "brooks service" → Daniel Brooks › Service History.
//
// The obstacle is that matching is token-AND: "service" appears nowhere on
// Daniel Brooks' record, so asking /search for "brooks service" correctly
// returns nothing, and there is no member row left to hang a tab off. Indexing
// every section label onto every member would fix that and ruin the index — a
// bare search for "qualifications" would then return the entire roster.
//
// So the query is SPLIT instead. Tokens that name a member section are peeled
// off, the remainder is asked of /search as an ordinary member lookup, and the
// two halves are recombined here. One extra request, only when someone actually
// types a section word, and the index stays clean.
//
// The section list itself is client-side and per-member (pages/member/sections.js
// gates on rank, features and the viewer's permissions), which is the other
// reason this can't live in the api: the api would have to own the client's
// route table to know that Service History is FirefighterRanks-only.

// Aliases people type that the label doesn't contain. The label is always
// matched too, so this only has to cover the gaps.
const SECTION_ALIASES = {
    edit: ['profile', 'details', 'contact', 'address', 'phone', 'email'],
    serviceHistory: ['service', 'seniority', 'years', 'rank history', 'status'],
    qualifications: ['quals', 'skills', 'certs', 'certifications', 'matrix'],
    driverClearance: ['driving', 'driver', 'clearance', 'wheel'],
    records: ['hr', 'human resources', 'awards', 'discipline', 'notes'],
    incidents: ['calls', 'responses', 'attendance'],
    training: ['practices', 'sessions', 'attendance'],
    transcript: ['jibc', 'export'],
    jibcConsent: ['jibc', 'consent'],
    history: ['audit', 'changes', 'log'],
    permissions: ['access', 'roles'],
    tasks: ['todo', 'to do'],
    assignments: ['assigned'],
    gear: ['equipment', 'kit', 'issued']
};

const sectionHaystack = section => [section.label, ...(SECTION_ALIASES[section.id] || [])]
    .join(' ')
    .toLowerCase();

// Every section that EXISTS, ungated — used only to recognise a section word in
// the query. Whether this particular member actually has the tab is decided
// per-member in the expansion below, against the real gated list.
const ALL_SECTION_HAYSTACKS = MEMBER_SECTIONS
    .filter(section => section.id !== DEFAULT_MEMBER_SECTION)
    .map(sectionHaystack);

const namesASection = token => ALL_SECTION_HAYSTACKS.some(haystack => haystack.includes(token));

/**
 * Split "brooks service" into the member half and the section half.
 *
 * Returns null unless BOTH halves are non-empty — "service history" is two
 * section words and no name, "brooks" is a name and no section, and neither is a
 * request for a specific tab. That guard is what stops one member fanning out
 * into fourteen near-identical rows.
 */
export const splitMemberTabQuery = query => {
    const tokens = tokenize(query);
    if (tokens.length < 2) return null;

    const sectionTokens = tokens.filter(namesASection);
    const nameTokens = tokens.filter(token => !sectionTokens.includes(token));

    if (!sectionTokens.length || !nameTokens.length) return null;
    return {nameQuery: nameTokens.join(' '), nameTokens, sectionTokens};
};

export default function useMemberTabResults() {
    const {currentUser} = useContext(UserContext);
    const settings = useContext(SettingsContext);
    const {usesJIBC} = settings;
    // Driving hours (#1580) hang off Requirements AND their own opt-in toggle.
    const driverTrainingHours = hasFeature('requirements') && isTrackingEnabled(settings);

    return useCallback((split, members = []) => {
        if (!split || !members.length) return [];
        const {nameTokens, sectionTokens} = split;
        const results = [];

        for (const member of members) {
            const sections = visibleMemberSections({
                currentUser,
                isProfile: false,
                isNew: false,
                archived: member.meta?.archived === true,
                usesJIBC,
                role: member.meta?.role || null,
                driverTrainingHours,
                hasFeature
            });

            for (const section of sections) {
                // The profile form is what the plain member row already opens —
                // offering it again as a "tab" is a duplicate row.
                if (section.id === DEFAULT_MEMBER_SECTION) continue;

                const haystack = sectionHaystack(section);
                if (!sectionTokens.every(token => haystack.includes(token))) continue;

                results.push({
                    type: 'members',
                    id: `${member.id}:${section.id}`,
                    url: memberSectionPath(memberBasePath(false, member.id), section.id),
                    title: member.title,
                    subtitle: section.label,
                    date: null,
                    status: null,
                    meta: member.meta,
                    // Above the plain member row: someone who typed a section
                    // name wants the section, not the top of the profile.
                    score: (member.score || 0) + 100,
                    highlight: splitHighlight(member.title, nameTokens),
                    // Rendered as the tab chip, which is what makes the row read
                    // as "this opens a tab" rather than as a second person.
                    snippet: {label: section.label, tab: '', pre: '', hit: '', post: ''},
                    isTab: true
                });
            }
        }

        return results;
    }, [currentUser, usesJIBC, driverTrainingHours]);
}
